import React, { useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Popover,
  Button,
  Menu,
  MenuItem,
  Tag,
  Text,
  Tooltip,
  Position
} from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { usePresence } from '../../hooks/usePresence';
import { useAppStore } from '../../stores/UnifiedStoreProvider';
import { stringToColor } from '../../utils/stringToColor';
import './Presence.css';

interface AvatarPresenceIndicatorProps {
  userId: string;
}

/**
 * Fixed AvatarPresenceIndicator that works with WebSocket presence
 * Shows horizontal avatar stack of online users
 */
const AvatarPresenceIndicatorFixed: React.FC<AvatarPresenceIndicatorProps> = observer(({ userId }) => {
  const appStore = useAppStore();
  const { auth } = appStore;
  const currentUser = auth.user;
  
  // Get presence data from WebSocket>
  const { user: myPresence, peers } = usePresence({ 
    roomType: 'project', 
    roomId: 'global' 
  });
  
  // Generate avatar URL for a user
  const generateAvatarUrl = (userId: string, name?: string) => {
    const displayName = name || userId.split('@')[0] || 'User';
    const color = stringToColor(userId, { saturation: 60, lightness: 45 });
    const bgColor = color.replace('#', '');
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=${bgColor}&color=fff&size=64`;
  };
  
  // Format last online time
  const formatLastOnline = (timestamp?: number) => {
    if (!timestamp) return 'Unknown';
    
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };
  
  // Get ALL users (online + offline with history) from OnlinePresenceStore
  const allUsers = useMemo(() => {
    const storeUsers = appStore.onlinePresence.allUsersHistory;
    
    return storeUsers.map(user => {
      // Look up org member for actual name
      const orgMember = appStore.user.getUserById(user.userId);
      const displayName = orgMember?.displayName || user.displayName || user.email || 'User';
      
      return {
        id: user.userId,
        name: displayName,
        email: orgMember?.email || user.email,
        src: generateAvatarUrl(user.userId, displayName),
        isOnline: user.isOnline,
        isSelf: user.userId === currentUser?.id,
        lastActivity: user.lastSeen ? new Date(user.lastSeen).getTime() : Date.now(),
        lastSeenText: user.lastSeenText,
        status: user.isOnline ? 'active' : 'offline',
        sessions: user.sessions,
        sessionCount: user.sessions.length,
      };
    }).sort((a, b) => {
      // Self first
      if (a.isSelf) return -1;
      if (b.isSelf) return 1;
      // Online users next
      if (a.isOnline && !b.isOnline) return -1;
      if (!a.isOnline && b.isOnline) return 1;
      // Then by last activity
      return b.lastActivity - a.lastActivity;
    });
  }, [currentUser, appStore.onlinePresence.allUsersHistory, appStore.user.users]);
  
  // Count online users for button text
  const onlineCount = allUsers.filter(u => u.isOnline).length;
  
  // Build presence menu
  const presenceMenu = useMemo(() => {
    const onlineUsersList = allUsers.filter(u => u.isOnline);
    const offlineUsersList = allUsers.filter(u => !u.isOnline);
    
    return (
      <Menu className="presence-menu">
        <MenuItem 
          text={`Online Now (${onlineUsersList.length})`} 
          disabled={true} 
          className="bp5-heading" 
        />
        
        {onlineUsersList.map(user => (
          <MenuItem
            key={user.id}
            text={user.name}
            className="presence-user-item"
            labelElement={
              <div className="presence-user-container">
                <img 
                  src={user.src}
                  alt={user.name} 
                  className="presence-avatar"
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    marginRight: '8px'
                  }}
                />
                <div className="presence-user-details">
                  <div className="presence-user-name">
                    {user.name}
                    {user.isSelf && <Tag minimal intent="primary" style={{ marginLeft: 4 }}>You</Tag>}
                    {user.sessionCount > 1 && (
                      <Tag minimal intent="success" style={{ marginLeft: 4 }}>
                        {user.sessionCount} sessions
                      </Tag>
                    )}
                  </div>
                  <div className="presence-status-indicator">
                    <div 
                      className="presence-status-dot" 
                      style={{ 
                        backgroundColor: '#36B37E',
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        display: 'inline-block',
                        marginRight: '4px'
                      }}
                    />
                    <Text className="presence-status-text">
                      {user.status === 'active' ? 'Active now' : 
                       user.status === 'idle' ? `Idle • ${formatLastOnline(user.lastActivity)}` :
                       'Online'}
                    </Text>
                  </div>
                  {user.email && (
                    <Text className="presence-user-email" style={{ fontSize: '11px', opacity: 0.7 }}>
                      {user.email}
                    </Text>
                  )}
                  
                  {/* Session details */}
                  {user.sessions && user.sessions.length > 0 && (
                    <div style={{ marginTop: '8px', fontSize: '11px', opacity: 0.8 }}>
                      {user.sessions.map((session: any, idx: number) => (
                        <div key={session.sessionId} style={{ 
                          marginTop: idx > 0 ? '6px' : 0,
                          paddingTop: idx > 0 ? '6px' : 0,
                          borderTop: idx > 0 ? '1px solid rgba(255,255,255,0.1)' : 'none'
                        }}>
                          <div>{session.browserName} • {session.deviceType}</div>
                          <div style={{ marginTop: '2px' }}>📍 {session.locationString}</div>
                          <div style={{ marginTop: '2px', opacity: 0.6 }}>🌐 {session.ip}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            }
          />
        ))}
        
        {onlineUsersList.length === 0 && (
          <MenuItem text="No users online" disabled />
        )}
        
        {/* Show offline users with history */}
        {offlineUsersList.length > 0 && (
          <>
            <MenuItem 
              text={`Recently Active (${offlineUsersList.length})`} 
              disabled={true} 
              className="bp5-heading" 
              style={{ marginTop: '8px', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '8px' }}
            />
            
            {offlineUsersList.map(user => (
              <MenuItem
                key={user.id}
                text={user.name}
                className="presence-user-item"
                labelElement={
                  <div className="presence-user-container">
                    <img 
                      src={user.src}
                      alt={user.name} 
                      className="presence-avatar"
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        marginRight: '8px',
                        opacity: 0.5
                      }}
                    />
                    <div className="presence-user-details">
                      <div className="presence-user-name">{user.name}</div>
                      <div className="presence-status-indicator">
                        <div 
                          className="presence-status-dot" 
                          style={{ 
                            backgroundColor: '#8A9BA8',
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            display: 'inline-block',
                            marginRight: '4px'
                          }}
                        />
                        <Text className="presence-status-text" style={{ fontSize: '11px', opacity: 0.7 }}>
                          Last seen {user.lastSeenText}
                        </Text>
                      </div>
                      {user.email && (
                        <Text className="presence-user-email" style={{ fontSize: '11px', opacity: 0.6 }}>
                          {user.email}
                        </Text>
                      )}
                    </div>
                  </div>
                }
              />
            ))}
          </>
        )}
      </Menu>
    );
  }, [allUsers]);
  
  // Render individual avatar with tooltip
  const renderAvatar = (user: any, index: number) => {
    return (
      <Tooltip
        key={user.id}
        content={
          <div className="avatar-tooltip">
            <div className="avatar-tooltip-name">{user.name} {user.isSelf && '(You)'}</div>
            <div className="avatar-tooltip-status">
              {user.isOnline ? 'Online now' : `Last seen ${user.lastSeenText}`}
            </div>
          </div>
        }
        position={Position.BOTTOM}
        openOnTargetFocus={false}
        hoverOpenDelay={300}
        hoverCloseDelay={100}
      >
        <div style={{ display: 'inline-block', position: 'relative' }}>
          <img 
            src={user.src}
            alt={user.name}
            style={{
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              border: '2px solid #36B37E',
              marginRight: '-10px',
              zIndex: onlineCount - index,
              position: 'relative',
              backgroundColor: '#f0f0f0'
            }}
          />
          {user.isSelf && (
            <div style={{
              position: 'absolute',
              bottom: -2,
              right: -2,
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              backgroundColor: '#36B37E',
              border: '2px solid white',
              zIndex: 100
            }} />
          )}
        </div>
      </Tooltip>
    );
  };
  
  return (
    <div className="avatar-presence-indicator" style={{ display: 'inline-flex', alignItems: 'center' }}>
      <Popover 
        content={presenceMenu} 
        position="bottom"
        interactionKind="click"
        autoFocus={false}
        enforceFocus={false}
        hasBackdrop={false}
        captureDismiss={false}
        popoverClassName="presence-menu-popover"
      >
        <Button
          minimal
          className="presence-button"
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            padding: '4px 12px',
            cursor: 'pointer'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {/* Show up to 5 avatars of ONLINE users only */}
            {allUsers.filter(u => u.isOnline).slice(0, 5).map((user, index) => renderAvatar(user, index))}
            
            {/* Show overflow count if more than 5 online users */}
            {onlineCount > 5 && (
              <div style={{ 
                width: '28px', 
                height: '28px', 
                borderRadius: '50%', 
                backgroundColor: 'var(--background-alt, #394B59)', 
                color: 'var(--text-primary, #FFFFFF)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: 'bold',
                marginLeft: '4px',
                border: '2px solid transparent'
              }}>
                +{onlineCount - 5}
              </div>
            )}
            
            {/* Online count text */}
            <div style={{ 
              marginLeft: '12px',
              fontSize: '13px',
              fontWeight: 500,
              color: 'var(--text-secondary, #8A9BA8)'
            }}>
              {onlineCount} online
            </div>
          </div>
        </Button>
      </Popover>
    </div>
  );
});

export default AvatarPresenceIndicatorFixed;
